import React, { useEffect, useState } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Briefcase, Banknote, TrendingUp, Building2, Factory, 
  ShoppingBag, Award, Wallet, Download, ArrowLeft, Printer, MapPin, FileSpreadsheet 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import * as XLSX from 'xlsx';
import { openPrintWindow } from '../utils/printUtils';

const StatisticsReport = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const navigate = useNavigate();

  useEffect(() => { fetchBranches(); }, []);
  useEffect(() => { fetchStats(); }, [selectedBranch]);

  const fetchBranches = async () => {
    try { const r = await api.get('/branches'); setBranches(r.data); } catch (e) { console.warn('Failed to load branches', e); }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBranch && selectedBranch !== 'all') params.branch_id = selectedBranch;
      const r = await api.get('/dashboard/stats', { params });
      setStats(r.data);
    } catch { toast.error('Failed to fetch statistics'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => {
    if (selectedBranch === 'all') return 'All Branches';
    return branches.find(b => b.id === selectedBranch)?.name || 'Unknown';
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.setTextColor(4,120,87);
    doc.text('WWAGS - Wangjing Women and Girls Society', 105, 18, { align: 'center' });
    doc.setFontSize(11); doc.setTextColor(80,80,80);
    doc.text('Business Resource Center (BRC) - MIS Portal', 105, 26, { align: 'center' });
    doc.setFontSize(14); doc.setTextColor(0,0,0);
    doc.text('Statistics Report', 105, 36, { align: 'center' });
    doc.setFontSize(9); doc.setTextColor(100,100,100);
    doc.text(`Branch: ${getBranchName()}  |  Date: ${new Date().toLocaleDateString('en-IN', { day:'2-digit',month:'long',year:'numeric' })}`, 105, 43, { align: 'center' });

    const staffData = [
      ['Total BRC-MC Members', stats?.total_brc_mc || 0],
      ['CRP-EPs Engaged', stats?.total_crp_eps_engaged || 0],
      ['CRP-EPs Active Working', stats?.total_crp_eps_active || 0],
    ];
    doc.setFontSize(11); doc.setTextColor(4,120,87); doc.text('A. STAFF STATISTICS', 14, 54);
    autoTable(doc, { startY: 58, head: [['Particulars','Count']], body: staffData, theme:'grid', headStyles:{fillColor:[4,120,87],fontSize:9}, bodyStyles:{fontSize:9}, columnStyles:{1:{halign:'center'}} });

    const entData = [
      ['Approved BP (CEF/CIF/Bank)', stats?.total_approved_bp || 0],
      ['Promoted Enterprises (Grounded)', stats?.total_promoted_enterprises || 0],
      ['Manufacturing', stats?.total_manufacturing || 0],
      ['Trading', stats?.total_trading || 0],
      ['Service', stats?.total_service || 0],
      ['Group Enterprises', stats?.total_group_enterprises || 0],
      ['Existing Enterprises', stats?.total_existing_enterprises || 0],
      ['New Enterprises', stats?.total_new_enterprises || 0],
      ['Women Led Enterprises', stats?.total_women_led || 0],
      ['Sub-Sector Based Enterprises', stats?.total_sub_sector_enterprises || 0],
      ['Convergent Enterprises', stats?.total_convergent_enterprises || 0],
      ['Shut Down Enterprises', stats?.total_shut_down || 0],
      ['SC/ST/OBC Entrepreneurs', stats?.total_sc_st_obc || 0],
    ];
    let y = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(11); doc.setTextColor(194,65,12); doc.text('B. ENTERPRISE STATISTICS', 14, y);
    autoTable(doc, { startY: y+4, head: [['Particulars','Count']], body: entData, theme:'grid', headStyles:{fillColor:[194,65,12],fontSize:9}, bodyStyles:{fontSize:9}, columnStyles:{1:{halign:'center'}} });

    const finData = [
      ['CEF Loan Outstanding', `Rs. ${(stats?.total_cef_outstanding||0).toLocaleString('en-IN')}`],
      ['Cash at Bank', `Rs. ${(stats?.cash_at_bank||0).toLocaleString('en-IN')}`],
      ['Cash in Hand', `Rs. ${(stats?.cash_in_hand||0).toLocaleString('en-IN')}`],
      ['Total Cash Available', `Rs. ${((stats?.cash_at_bank||0)+(stats?.cash_in_hand||0)).toLocaleString('en-IN')}`],
    ];
    y = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(11); doc.setTextColor(37,99,235); doc.text('C. FINANCIAL STATISTICS', 14, y);
    autoTable(doc, { startY: y+4, head: [['Particulars','Amount (Rs.)']], body: finData, theme:'grid', headStyles:{fillColor:[37,99,235],fontSize:9}, bodyStyles:{fontSize:9}, columnStyles:{1:{halign:'right'}} });

    doc.setFontSize(8); doc.setTextColor(120,120,120);
    doc.text('MIS Developed by Institute of Skill Development and Entrepreneurship (ISDE), Manipur', 105, doc.internal.pageSize.height - 10, { align: 'center' });
    doc.save(`Statistics_Report_${getBranchName().replace(/\s+/g,'_')}.pdf`);
    toast.success('PDF downloaded');
  };

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const data = [
      ['WWAGS - Wangjing Women and Girls Society'],
      ['Business Resource Center (BRC) - Statistics Report'],
      [`Branch: ${getBranchName()}`],
      [`Generated: ${new Date().toLocaleDateString('en-IN')}`],
      [],
      ['A. STAFF STATISTICS'],
      ['Particulars','Count'],
      ['Total BRC-MC Members', stats?.total_brc_mc || 0],
      ['CRP-EPs Engaged', stats?.total_crp_eps_engaged || 0],
      ['CRP-EPs Active Working', stats?.total_crp_eps_active || 0],
      [],
      ['B. ENTERPRISE STATISTICS'],
      ['Particulars','Count'],
      ['Approved BP (CEF/CIF/Bank)', stats?.total_approved_bp || 0],
      ['Promoted Enterprises (Grounded)', stats?.total_promoted_enterprises || 0],
      ['Manufacturing', stats?.total_manufacturing || 0],
      ['Trading', stats?.total_trading || 0],
      ['Service', stats?.total_service || 0],
      ['Group Enterprises', stats?.total_group_enterprises || 0],
      ['Existing Enterprises', stats?.total_existing_enterprises || 0],
      ['New Enterprises', stats?.total_new_enterprises || 0],
      ['Women Led Enterprises', stats?.total_women_led || 0],
      ['Sub-Sector Based Enterprises', stats?.total_sub_sector_enterprises || 0],
      ['Convergent Enterprises', stats?.total_convergent_enterprises || 0],
      ['Shut Down Enterprises', stats?.total_shut_down || 0],
      ['SC/ST/OBC Entrepreneurs', stats?.total_sc_st_obc || 0],
      [],
      ['C. FINANCIAL STATISTICS'],
      ['Particulars','Amount (Rs.)'],
      ['CEF Loan Outstanding', stats?.total_cef_outstanding || 0],
      ['Cash at Bank', stats?.cash_at_bank || 0],
      ['Cash in Hand', stats?.cash_in_hand || 0],
      ['Total Cash Available', (stats?.cash_at_bank||0)+(stats?.cash_in_hand||0)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{wch:40},{wch:20}];
    XLSX.utils.book_append_sheet(wb, ws, 'Statistics');
    XLSX.writeFile(wb, 'Statistics_Report.xlsx');
    toast.success('Excel downloaded');
  };

  const handlePrint = () => {
    if (!stats) return;
    const br = getBranchName();
    const date = new Date().toLocaleDateString('en-IN', { day:'2-digit',month:'long',year:'numeric' });

    const html = `<html><head><title>Statistics Report - WWAGS MIS</title>
    <style>
      body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:30px 40px;color:#1e293b;font-size:11px}
      .header{text-align:center;border-bottom:3px double #047857;padding-bottom:12px;margin-bottom:20px}
      .header h1{color:#047857;font-size:18px;margin:0 0 4px}
      .header h2{color:#475569;font-size:12px;margin:0 0 4px;font-weight:normal}
      .header h3{color:#1e293b;font-size:14px;margin:8px 0 0;font-weight:600}
      .meta{text-align:center;font-size:10px;color:#64748b;margin-bottom:18px}
      .section{margin-bottom:16px}
      .section-title{font-size:12px;font-weight:700;color:#fff;padding:6px 12px;margin-bottom:0;border-radius:4px 4px 0 0}
      .section-title.staff{background:#047857}
      .section-title.enterprise{background:#c2410c}
      .section-title.financial{background:#2563eb}
      table{width:100%;border-collapse:collapse;margin-bottom:16px}
      th{background:#f1f5f9;padding:6px 10px;text-align:left;font-size:10px;font-weight:600;color:#334155;border:1px solid #e2e8f0}
      td{padding:5px 10px;border:1px solid #e2e8f0;font-size:10px}
      td:last-child{text-align:right;font-weight:600;width:100px}
      tr:nth-child(even){background:#f8fafc}
      .total-row{background:#ecfdf5!important;font-weight:700}
      .total-row td{border-top:2px solid #047857;font-size:11px}
      .footer{text-align:center;font-size:8px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:10px;margin-top:20px}
      @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{margin:15mm}}
    </style></head><body>
    <div class="header">
      <h1>WWAGS - Wangjing Women and Girls Society</h1>
      <h2>Business Resource Center (BRC) - MIS Portal</h2>
      <h3>Statistics Report</h3>
    </div>
    <div class="meta">Branch: <b>${br}</b> &nbsp;|&nbsp; Report Date: <b>${date}</b></div>

    <div class="section">
      <div class="section-title staff">A. Staff Statistics</div>
      <table>
        <thead><tr><th>Sl.</th><th>Particulars</th><th>Count</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>Total BRC-MC Members</td><td>${stats?.total_brc_mc||0}</td></tr>
          <tr><td>2</td><td>CRP-EPs Engaged</td><td>${stats?.total_crp_eps_engaged||0}</td></tr>
          <tr><td>3</td><td>CRP-EPs Active Working</td><td>${stats?.total_crp_eps_active||0}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="section">
      <div class="section-title enterprise">B. Enterprise Statistics</div>
      <table>
        <thead><tr><th>Sl.</th><th>Particulars</th><th>Count</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>Approved BP (CEF/CIF/Bank Loan)</td><td>${stats?.total_approved_bp||0}</td></tr>
          <tr><td>2</td><td>Promoted Enterprises (Grounded)</td><td>${stats?.total_promoted_enterprises||0}</td></tr>
          <tr><td>3</td><td>Manufacturing Enterprises</td><td>${stats?.total_manufacturing||0}</td></tr>
          <tr><td>4</td><td>Trading Enterprises</td><td>${stats?.total_trading||0}</td></tr>
          <tr><td>5</td><td>Service Enterprises</td><td>${stats?.total_service||0}</td></tr>
          <tr><td>6</td><td>Group Enterprises</td><td>${stats?.total_group_enterprises||0}</td></tr>
          <tr><td>7</td><td>Existing Enterprises</td><td>${stats?.total_existing_enterprises||0}</td></tr>
          <tr><td>8</td><td>New Enterprises</td><td>${stats?.total_new_enterprises||0}</td></tr>
          <tr><td>9</td><td>Women Led Enterprises</td><td>${stats?.total_women_led||0}</td></tr>
          <tr><td>10</td><td>Sub-Sector Based Enterprises</td><td>${stats?.total_sub_sector_enterprises||0}</td></tr>
          <tr><td>11</td><td>Convergent Enterprises</td><td>${stats?.total_convergent_enterprises||0}</td></tr>
          <tr><td>12</td><td>Shut Down Enterprises</td><td>${stats?.total_shut_down||0}</td></tr>
          <tr><td>13</td><td>SC/ST/OBC Entrepreneurs</td><td>${stats?.total_sc_st_obc||0}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="section">
      <div class="section-title financial">C. Financial Statistics</div>
      <table>
        <thead><tr><th>Sl.</th><th>Particulars</th><th>Amount (Rs.)</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>CEF Loan Outstanding</td><td>Rs.${(stats?.total_cef_outstanding||0).toLocaleString('en-IN')}</td></tr>
          <tr><td>2</td><td>Cash at Bank</td><td>Rs.${(stats?.cash_at_bank||0).toLocaleString('en-IN')}</td></tr>
          <tr><td>3</td><td>Cash in Hand</td><td>Rs.${(stats?.cash_in_hand||0).toLocaleString('en-IN')}</td></tr>
          <tr class="total-row"><td></td><td>Total Cash Available</td><td>Rs.${((stats?.cash_at_bank||0)+(stats?.cash_in_hand||0)).toLocaleString('en-IN')}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="footer">MIS Developed by Institute of Skill Development and Entrepreneurship (ISDE), Manipur<br/>(An Institute run under Shri Suvakumar Singh Memorial Trust, SSSMT)</div>
    </body></html>`;
    openPrintWindow(html);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="statistics-report">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 print:mb-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/reports')} className="print:hidden" data-testid="back-to-reports-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div>
            <p className="text-sm text-emerald-700 font-medium">WWAGS - Wangjing Women and Girls Society</p>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Statistics Report</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <p className="text-xs text-slate-500">
                {new Date().toLocaleDateString('en-IN', { day:'2-digit',month:'long',year:'numeric' })}
              </p>
              {stats?.branch_name && (
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <MapPin className="w-3 h-3" />{stats.branch_name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          {isAdmin() && (
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-52" data-testid="branch-filter-select"><SelectValue placeholder="Select Branch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={handlePrint} data-testid="print-report-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button onClick={downloadPDF} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-1" />PDF</Button>
          <Button onClick={downloadExcel} variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="download-excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
        </div>
      </div>

      {/* Professional 2-Column Layout */}
      <div className="space-y-6">
        {/* Row 1: Staff (left) + Financial (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* A. Staff Statistics */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-emerald-700 px-5 py-3">
              <h2 className="text-sm font-bold text-white tracking-wide">A. STAFF STATISTICS</h2>
            </div>
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 w-10">Sl.</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600">Particulars</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 w-20">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ['Total BRC-MC Members', stats?.total_brc_mc],
                  ['CRP-EPs Engaged', stats?.total_crp_eps_engaged],
                  ['CRP-EPs Active Working', stats?.total_crp_eps_active],
                ].map(([label, val], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="px-4 py-2.5 text-sm text-slate-500">{i+1}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-800">{label}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-bold text-slate-900">{val || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* C. Financial Statistics */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-blue-700 px-5 py-3">
              <h2 className="text-sm font-bold text-white tracking-wide">C. FINANCIAL STATISTICS</h2>
            </div>
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 w-10">Sl.</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600">Particulars</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 w-32">Amount (Rs.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr className="bg-white">
                  <td className="px-4 py-2.5 text-sm text-slate-500">1</td>
                  <td className="px-4 py-2.5 text-sm text-slate-800">CEF Loan Outstanding</td>
                  <td className="px-4 py-2.5 text-sm text-right font-bold text-red-700">Rs.{(stats?.total_cef_outstanding||0).toLocaleString('en-IN')}</td>
                </tr>
                <tr className="bg-slate-50/50">
                  <td className="px-4 py-2.5 text-sm text-slate-500">2</td>
                  <td className="px-4 py-2.5 text-sm text-slate-800">Cash at Bank</td>
                  <td className="px-4 py-2.5 text-sm text-right font-bold text-slate-900">Rs.{(stats?.cash_at_bank||0).toLocaleString('en-IN')}</td>
                </tr>
                <tr className="bg-white">
                  <td className="px-4 py-2.5 text-sm text-slate-500">3</td>
                  <td className="px-4 py-2.5 text-sm text-slate-800">Cash in Hand</td>
                  <td className="px-4 py-2.5 text-sm text-right font-bold text-slate-900">Rs.{(stats?.cash_in_hand||0).toLocaleString('en-IN')}</td>
                </tr>
                <tr className="bg-emerald-50 border-t-2 border-emerald-300">
                  <td className="px-4 py-3 text-sm"></td>
                  <td className="px-4 py-3 text-sm font-bold text-emerald-900">Total Cash Available</td>
                  <td className="px-4 py-3 text-base text-right font-bold text-emerald-800">Rs.{((stats?.cash_at_bank||0)+(stats?.cash_in_hand||0)).toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Row 2: Enterprise Statistics (full width) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-orange-700 px-5 py-3">
            <h2 className="text-sm font-bold text-white tracking-wide">B. ENTERPRISE STATISTICS</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 w-10">Sl.</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600">Particulars</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 w-20">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ['Approved BP (CEF/CIF/Bank Loan)', stats?.total_approved_bp],
                  ['Promoted Enterprises (Grounded)', stats?.total_promoted_enterprises],
                  ['Manufacturing Enterprises', stats?.total_manufacturing],
                  ['Trading Enterprises', stats?.total_trading],
                  ['Service Enterprises', stats?.total_service],
                  ['Group Enterprises', stats?.total_group_enterprises],
                  ['Existing Enterprises', stats?.total_existing_enterprises],
                ].map(([label, val], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="px-4 py-2.5 text-sm text-slate-500">{i+1}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-800">{label}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-bold text-slate-900">{val || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="w-full border-l border-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 w-10">Sl.</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600">Particulars</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 w-20">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ['New Enterprises', stats?.total_new_enterprises],
                  ['Women Led Enterprises', stats?.total_women_led],
                  ['Sub-Sector Based Enterprises', stats?.total_sub_sector_enterprises],
                  ['Convergent Enterprises', stats?.total_convergent_enterprises],
                  ['Shut Down Enterprises', stats?.total_shut_down],
                  ['SC/ST/OBC Entrepreneurs', stats?.total_sc_st_obc],
                ].map(([label, val], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="px-4 py-2.5 text-sm text-slate-500">{i+8}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-800">{label}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-bold text-slate-900">{val || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Repayment Summary if available */}
        {stats?.repayment_data && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-purple-700 px-5 py-3">
              <h2 className="text-sm font-bold text-white tracking-wide">D. REPAYMENT & COLLECTION STATUS</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-slate-100">
              {[
                { label: 'Total Loans', value: stats.repayment_data.total_loans, color: 'text-slate-900' },
                { label: 'Total Disbursed', value: `Rs.${(stats.repayment_data.total_disbursed||0).toLocaleString('en-IN')}`, color: 'text-blue-700' },
                { label: 'Expected Collection', value: `Rs.${(stats.repayment_data.expected_collection||0).toLocaleString('en-IN')}`, color: 'text-amber-700' },
                { label: 'Actual Collected', value: `Rs.${(stats.repayment_data.actual_collected||0).toLocaleString('en-IN')}`, color: 'text-emerald-700' },
              ].map((item, i) => (
                <div key={i} className="p-4 text-center">
                  <p className="text-xs text-slate-500 font-medium mb-1">{item.label}</p>
                  <p className={`text-lg font-bold ${item.color}`}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center mt-6 text-xs text-slate-400">
        MIS Developed by Institute of Skill Development and Entrepreneurship (ISDE), Manipur
      </div>
    </div>
  );
};

export default StatisticsReport;
